/**
 * ReportingView — the Audit module's Reporting tab.
 *
 * One sticky filter bar drives everything: the filters object becomes one
 * query string, every section fetches its own /api/audit/analytics endpoint
 * with it, and the export links carry the same query so what you download is
 * exactly what you are looking at.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { API, buildAnalyticsQuery } from './ReportingShared.jsx';
import { SEVERITY_LABELS, CLASSIFICATION_LABELS, PROGRAM_LABELS } from './labels.js';
import KpiRow from './ReportingKpis.jsx';
import { PlanSection, CycleTimesSection } from './ReportingSectionsPlan.jsx';
import { FindingsSection, CapsSection } from './ReportingSectionsFindings.jsx';
import { UniverseSection, EvidenceSection, TrailSection } from './ReportingSectionsQuality.jsx';
import RegisterSection from './ReportingRegister.jsx';

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - i);
const PROGRAMS = ['medicare_advantage', 'part_d', 'medicaid', 'dual_dsnp', 'other'];

function Filter({ label, value, onChange, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-0.5">{label}</span>
      <select aria-label={label} value={value} onChange={e => onChange(e.target.value)}
        className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 text-slate-700 bg-white min-w-[110px]">
        {children}
      </select>
    </label>
  );
}

function FilterBar({ filters, setFilters, templates, members, lockedSubjectType }) {
  const set = (k) => (v) => setFilters(f => ({ ...f, [k]: v }));
  return (
    <div className="sticky top-0 z-20 bg-white border border-slate-200 rounded-xl px-4 py-3 shadow-sm">
      <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
        <Filter label="Year" value={filters.year} onChange={set('year')}>
          <option value="">All years</option>
          {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
        </Filter>
        <Filter label="Quarter" value={filters.quarter} onChange={set('quarter')}>
          <option value="">All quarters</option>
          {['Q1', 'Q2', 'Q3', 'Q4'].map(q => <option key={q} value={q}>{q}</option>)}
        </Filter>
        {!lockedSubjectType && (
          <Filter label="Subject type" value={filters.subject_type} onChange={set('subject_type')}>
            <option value="">All subjects</option>
            <option value="internal">Internal</option>
            <option value="fdr">FDR</option>
          </Filter>
        )}
        <Filter label="Program" value={filters.program} onChange={set('program')}>
          <option value="">All programs</option>
          {PROGRAMS.map(p => <option key={p} value={p}>{PROGRAM_LABELS[p]}</option>)}
        </Filter>
        <Filter label="Severity" value={filters.severity} onChange={set('severity')}>
          <option value="">All severities</option>
          {['critical', 'high', 'medium', 'low'].map(s => <option key={s} value={s}>{SEVERITY_LABELS[s]}</option>)}
        </Filter>
        <Filter label="Classification" value={filters.classification} onChange={set('classification')}>
          <option value="">All classifications</option>
          {['observation', 'car', 'ids'].map(c => <option key={c} value={c}>{CLASSIFICATION_LABELS[c]}</option>)}
          <option value="unclassified">Unclassified</option>
        </Filter>
        <Filter label="Template" value={filters.template_id} onChange={set('template_id')}>
          <option value="">All templates</option>
          {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </Filter>
        <Filter label="Auditor" value={filters.auditor_id} onChange={set('auditor_id')}>
          <option value="">All auditors</option>
          {members.map(m => <option key={m.id} value={m.id}>{m.name || m.email}</option>)}
        </Filter>
      </div>
    </div>
  );
}

function ExportBar({ qs }) {
  const withQs = (path, sep) => `${API}/api/audit/analytics/${path}${qs ? `${sep}${qs}` : ''}`;
  const cls = 'text-xs font-semibold text-slate-600 hover:text-slate-800';
  return (
    <div className="bg-white border border-slate-200 rounded-xl px-4 py-2.5 flex flex-wrap items-center gap-x-5 gap-y-1.5">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Take it with you</span>
      <a className={cls} href={withQs('export.csv?dataset=register', '&')}>↓ Register (CSV)</a>
      <a className={cls} href={withQs('export.csv?dataset=findings', '&')}>↓ Findings (CSV)</a>
      <a className={cls} href={withQs('export.csv?dataset=caps', '&')}>↓ CAPs (CSV)</a>
      <a className="text-xs font-semibold text-orange-600 hover:text-orange-700" href={withQs('committee-report.docx', '?')}>↓ Committee report (Word)</a>
      <span className="text-[10px] text-slate-400 ml-auto">Exports honor the filters above.</span>
    </div>
  );
}

export default function ReportingView({ lockedSubjectType = '' }) {
  const [filters, setFilters] = useState({
    year: String(CURRENT_YEAR), quarter: '', subject_type: lockedSubjectType, program: '',
    severity: '', classification: '', template_id: '', auditor_id: '',
  });
  const [templates, setTemplates] = useState([]);
  const [members, setMembers] = useState([]);
  const qs = useMemo(() => buildAnalyticsQuery(filters), [filters]);

  // Template options: custom/learned templates carry the UUID the analytics
  // filter accepts (built-ins are keyed, not id'd, so they cannot be filtered).
  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/audit/templates`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (alive && d.success) setTemplates(d.custom || []); })
      .catch(() => {});
    fetch(`${API}/api/audit/team-members`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (alive && d.success) setMembers(d.members || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  return (
    <div className="space-y-4">
      <FilterBar filters={filters} setFilters={setFilters} templates={templates} members={members} lockedSubjectType={lockedSubjectType} />
      <KpiRow qs={qs} />
      <PlanSection qs={qs} />
      <FindingsSection qs={qs} />
      <CapsSection qs={qs} />
      <CycleTimesSection qs={qs} />
      <UniverseSection qs={qs} />
      <EvidenceSection qs={qs} />
      <TrailSection qs={qs} />
      <RegisterSection qs={qs} />
      <ExportBar qs={qs} />
    </div>
  );
}
